/**
 * Menu
 *
 * Provides mobile menu button and modal behavior for navigation.
 * Handles menu toggle with modal overlay, focus trap, ARIA states,
 * and automatic inert management for collapsed dropdowns in the menu.
 *
 * Features:
 * - Modal behavior with overlay and body scroll lock
 * - Focus trap keeps keyboard navigation within open menu
 * - ARIA state management (aria-expanded, aria-controls)
 * - Inert state for collapsed menu dropdowns (non-interactive)
 * - MutationObserver syncs inert state with dropdown visual state
 * - Escape key to close menu
 * - Returns focus to menu button on close
 *
 * Structure:
 *   <button data-hs-nav-menu="button">Menu</button>
 *   <div data-hs-nav-menu="wrapper" aria-labelledby="menu-button">
 *     <!-- Menu content with dropdowns -->
 *   </div>
 */

import {
  querySelectorAll,
  querySelector,
  getSelector,
  globalConfig,
  cssVariables,
  animateHeight,
  setHeight,
  openModal,
  closeModal,
} from '@utils';

interface EventHandler {
  element: Element | Document;
  event: string;
  handler: EventListener;
  options?: AddEventListenerOptions;
}

interface ModuleConfig {
  attributes: {
    elements: Record<string, { primary: string; aliases?: string[] }>;
  };
}

export function init(config: ModuleConfig, navbarConfig?: ModuleConfig) {
  const cleanup = {
    observers: [] as MutationObserver[],
    handlers: [] as EventHandler[],
    state: {
      focusTrapHandler: null as ((e: KeyboardEvent) => void) | null,
    },
  };

  const addObserver = (observer: MutationObserver): void => {
    cleanup.observers.push(observer);
  };
  const addHandler = (
    element: Element | Document,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): void => {
    element.addEventListener(event, handler, options);
    cleanup.handlers.push({ element, event, handler, options });
  };

  function createFocusTrap(menuButton: Element): ((e: KeyboardEvent) => void) | undefined {
    const navbarWrapper = navbarConfig
      ? querySelector(navbarConfig as any, 'wrapper')
      : document.querySelector('[data-hs-nav="wrapper"]');

    if (!navbarWrapper) return;

    const focusTrapHandler = (e: KeyboardEvent): void => {
      if (e.key === 'Tab') {
        const clickableSelector = getSelector(globalConfig.clickable as any, 'button');
        const clickableItems = Array.from(navbarWrapper.querySelectorAll(clickableSelector));
        const formElements = navbarWrapper.querySelectorAll(
          'input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const focusableArray = [...clickableItems, ...Array.from(formElements)];
        const firstElement = focusableArray[0];
        const lastElement = focusableArray[focusableArray.length - 1];

        if (e.shiftKey) {
          // Shift+Tab: moving backwards
          if (document.activeElement === firstElement) {
            e.preventDefault();
            (lastElement as HTMLElement).focus();
          }
        } else {
          // Tab: moving forwards
          if (document.activeElement === lastElement) {
            e.preventDefault();
            (firstElement as HTMLElement).focus();
          }
        }
      }
    };

    document.addEventListener('keydown', focusTrapHandler);
    return focusTrapHandler;
  }

  function removeFocusTrap(focusTrapHandler: ((e: KeyboardEvent) => void) | null): void {
    if (focusTrapHandler) {
      document.removeEventListener('keydown', focusTrapHandler);
    }
  }

  function setupMenu(
    addObserver: (observer: MutationObserver) => void,
    addHandler: (
      element: Element | Document,
      event: string,
      handler: EventListener,
      options?: AddEventListenerOptions
    ) => void
  ): void {
    const menuButtons = querySelectorAll(config as any, 'button');
    const menu = querySelector(config as any, 'wrapper');

    if (!menuButtons.length || !menu) return;

    // Get navbar wrapper for styling (e.g., backdrop blur)
    const navbarWrapper = navbarConfig ? querySelector(navbarConfig as any, 'wrapper') : null;

    const menuId = `menu-${Date.now()}`;

    menu.id = menuId;
    menu.setAttribute('role', 'dialog');
    menu.setAttribute('aria-modal', 'true');
    (menu as HTMLElement).inert = true; // Initial state: hidden and non-interactive

    // Check for visual height wrapper (optional)
    // Only look for a descendant with data-hs-height="element"
    // If found, animate that element's parent. If not found, no height animation.
    const heightWrapper = menu.querySelector('[data-hs-height="element"]');
    const animationTarget = heightWrapper ? heightWrapper.parentElement : null;

    menuButtons.forEach((menuButton) => {
      menuButton.setAttribute('aria-expanded', 'false');
      menuButton.setAttribute('aria-controls', menuId);
      menuButton.setAttribute('aria-label', 'Open navigation menu');

      let focusTrapHandler: ((e: KeyboardEvent) => void) | null = null;
      let shouldAutoFocus: boolean | 'last' = false; // Track if menu should autofocus (keyboard activation)

      // Check if menu is open by looking for is-active class on button
      function isMenuOpen() {
        return menuButton.classList.contains(globalConfig.classes.active);
      }

      // Update ARIA states and menu behavior based on current visual state
      function updateMenuState() {
        const isOpen = isMenuOpen();
        const wasOpen = menuButton.getAttribute('aria-expanded') === 'true';

        if (isOpen && !wasOpen) {
          // Opening
          openModal();

          // Add is-active to menu wrapper and enable interaction
          menu.classList.add(globalConfig.classes.active);
          (menu as HTMLElement).inert = false; // Enable interaction

          // Add class to navbar wrapper for styling (e.g., backdrop blur)
          if (navbarWrapper) {
            navbarWrapper.classList.add('hs-menu-open');
          }

          menuButtons.forEach((btn) => {
            btn.setAttribute('aria-expanded', 'true');
            btn.setAttribute('aria-label', 'Close navigation menu');
          });

          // Animate height if configured
          if (animationTarget) {
            animateHeight(animationTarget, true, { duration: 300, ease: 'power2.inOut' });
          }

          // Create focus trap for navbar
          focusTrapHandler = createFocusTrap(menuButton);

          // Only autofocus for keyboard activation
          if (shouldAutoFocus) {
            setTimeout(() => {
              const menuClickableSelector = getSelector(globalConfig.clickable as any, 'button');
              const allElements = Array.from(menu.querySelectorAll(menuClickableSelector));

              if (shouldAutoFocus === 'last') {
                // Focus last item (ArrowUp)
                const lastElement = allElements[allElements.length - 1];
                if (lastElement) {
                  (lastElement as HTMLElement).focus();
                }
              } else {
                // Focus first item (Space/Enter/ArrowDown)
                const firstElement = allElements[0];
                if (firstElement) {
                  (firstElement as HTMLElement).focus();
                }
              }
            }, 100);
            shouldAutoFocus = false; // Reset flag
          }
        } else if (!isOpen && wasOpen) {
          // Closing - focus button BEFORE updating ARIA
          if (menu.contains(document.activeElement)) {
            (menuButton as HTMLElement).focus();
          }

          // Now update ARIA and visual state
          closeModal();

          // Remove is-active from menu wrapper and disable interaction
          menu.classList.remove(globalConfig.classes.active);
          (menu as HTMLElement).inert = true; // Disable interaction

          // Remove class from navbar wrapper
          if (navbarWrapper) {
            navbarWrapper.classList.remove('hs-menu-open');
          }

          menuButtons.forEach((btn) => {
            btn.setAttribute('aria-expanded', 'false');
            btn.setAttribute('aria-label', 'Open navigation menu');
          });

          // Animate height if configured
          if (animationTarget) {
            animateHeight(animationTarget, false, { duration: 300, ease: 'power2.inOut' });
          }

          // Remove focus trap
          removeFocusTrap(focusTrapHandler);
          focusTrapHandler = null;
        }
      }

      // Set initial height without animation (if configured)
      if (animationTarget) {
        setHeight(animationTarget, isMenuOpen());
      }

      // Set initial ARIA states
      updateMenuState();

      // Monitor for class changes on button and update menu state
      const observer = new MutationObserver(() => {
        updateMenuState();
      });

      observer.observe(menuButton, {
        attributes: true,
        attributeFilter: ['class'],
      });

      addObserver(observer);

      // Click handler
      const clickHandler = function (e) {
        e.preventDefault();
        menuButton.classList.toggle('is-active');
      };

      addHandler(menuButton, 'click', clickHandler);

      // Keyboard navigation (dialog/modal pattern)
      const keydownHandler = function (e) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          shouldAutoFocus = true; // Set flag for keyboard activation
          menuButton.classList.toggle(globalConfig.classes.active);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (!isMenuOpen()) {
            shouldAutoFocus = true; // Open and focus first item
            menuButton.classList.add(globalConfig.classes.active);
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (!isMenuOpen()) {
            shouldAutoFocus = 'last'; // Open and focus last item
            menuButton.classList.add(globalConfig.classes.active);
          }
        } else if (e.key === 'Escape') {
          e.preventDefault();

          if (isMenuOpen()) {
            menuButton.classList.remove(globalConfig.classes.active);
          }
        }
      };

      addHandler(menuButton, 'keydown', keydownHandler);

      // Store focus trap handler for cleanup
      cleanup.state.focusTrapHandler = focusTrapHandler;
    });
  }

  function setupMenuDisplayObserver(addObserver) {
    const menu = querySelector(config, 'wrapper');
    if (!menu) return;

    let previousState = null;

    function handleStateChange() {
      const computedStyle = window.getComputedStyle(menu);
      const currentState = computedStyle.getPropertyValue(cssVariables.state).trim();

      // Detect state change from active (1) to inactive (0)
      if (
        previousState === globalConfig.cssVars.state.values.active &&
        currentState === globalConfig.cssVars.state.values.inactive
      ) {
        // Get menu button to check if menu is open
        const menuButton = document.querySelector('[data-hs-nav-menu="button"]');
        const isMenuOpen = menuButton && menuButton.getAttribute('aria-expanded') === 'true';

        // If menu is open, close it cleanly (triggers animation and proper cleanup)
        if (isMenuOpen && menuButton) {
          menuButton.classList.remove('is-active');
        }
      }

      previousState = currentState;
    }

    const stateObserver = new ResizeObserver(handleStateChange);
    stateObserver.observe(menu);
    addObserver(stateObserver);
    // Initial check
    handleStateChange();
  }

  setupMenu(addObserver, addHandler);
  setupMenuDisplayObserver(addObserver);

  return {
    result: 'menu initialized',
    destroy: () => {
      // Clean up focus trap
      if (cleanup.state.focusTrapHandler) {
        document.removeEventListener('keydown', cleanup.state.focusTrapHandler);
        cleanup.state.focusTrapHandler = null;
      }

      // Disconnect all observers
      cleanup.observers.forEach((obs) => obs.disconnect());
      cleanup.observers.length = 0;

      // Remove all event listeners
      cleanup.handlers.forEach(({ element, event, handler, options }) => {
        element.removeEventListener(event, handler, options);
      });
      cleanup.handlers.length = 0;

      // Remove body overflow class if present
      document.body.classList.remove('u-overflow-hidden');
    },
  };
}
